import { useState } from "react";
import { Plus, Trash2, Wallet } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { eur } from "@/lib/format";

/**
 * Owner-side return inspection + one-click deposit settlement.
 * Renders inside ReservationDetail once the client checkout exists.
 */
export default function ReturnSettlement({ reservation, onSaved }) {
  const r = reservation;
  const co = r.checkout_data || {};
  const kmCharge = Number(co.extra_charge || 0);
  const deposit = Number(r.deposit_amount || 0);
  const isHold = r.payment_status === "on_hold";

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fuel_ok: true,
    interior_ok: true,
    exterior_ok: true,
    water_ok: true,
    inventory_ok: true,
    notes: "",
    photos: [],
    include_km_charge: kmCharge > 0,
    owner_signature: "",
  });
  const [charges, setCharges] = useState([]); // [{concept, amount}]

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCharge = (i, k, v) =>
    setCharges((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));

  const manualTotal = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const total = manualTotal + (form.include_km_charge ? kmCharge : 0);
  const kept = Math.min(total, deposit);
  const released = Math.max(0, deposit - total);
  const over = Math.max(0, total - deposit);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        charges: charges
          .filter((c) => c.concept.trim() && Number(c.amount) > 0)
          .map((c) => ({ concept: c.concept.trim(), amount: Number(c.amount) })),
      };
      const { data } = await api.post(`/reservations/${r.id}/return-handover`, payload);
      if (data.total_charges > 0) {
        toast.success(`Liquidación completada: ${eur(data.total_charges)} retenidos. Email enviado al cliente.`);
      } else {
        toast.success("Fianza liberada íntegramente. Email enviado al cliente.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const settleLabel =
    total <= 0
      ? isHold ? "Liberar fianza completa" : "Devolver fianza completa"
      : `Retener ${eur(kept)} y ${isHold ? "liberar" : "devolver"} ${eur(released)}`;

  return (
    <div className="space-y-6" data-testid="return-settlement">
      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-1">
          Inspección de devolución
        </h3>
        <p className="text-sm text-[#5C5C5C] mb-4">
          Verifica el estado real del vehículo antes de liquidar la fianza. Tu
          inspección es la evidencia en caso de disputa.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {[
            ["fuel_ok", "Combustible al nivel de salida"],
            ["interior_ok", "Interior limpio y sin daños"],
            ["exterior_ok", "Exterior sin daños nuevos"],
            ["water_ok", "Depósitos de aguas gestionados"],
            ["inventory_ok", "Inventario completo"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center justify-between bg-[#F7F5F0] border border-[#DCD8D0] rounded-lg px-4 py-3 cursor-pointer">
              <span className="text-sm text-[#1A1A1A]">{label}</span>
              <Checkbox checked={form[k]} onCheckedChange={(v) => set(k, Boolean(v))} data-testid={`rs-${k}`} />
            </label>
          ))}
        </div>
        <div className="mb-4">
          <Label>Observaciones</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="rs-notes" />
        </div>
        <PhotoUploader
          label="Fotos de la inspección (daños nuevos, estado general)"
          value={form.photos}
          onChange={(v) => set("photos", v)}
          multiple
          adminUpload
          reservationId={reservation.id}
          testid="rs-photos"
        />
      </div>

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Cargos contra la fianza</h3>

        {kmCharge > 0 && (
          <label className="flex items-center justify-between bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-lg px-4 py-3 mb-3 cursor-pointer">
            <div className="text-sm">
              <div className="font-medium text-[#9C6512]">Exceso de kilometraje (automático)</div>
              <div className="text-xs text-[#9C6512]">{co.km_extra} km extra × {eur(r.extra_km_rate)}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-[#9C6512]">{eur(kmCharge)}</span>
              <Checkbox checked={form.include_km_charge} onCheckedChange={(v) => set("include_km_charge", Boolean(v))} data-testid="rs-km-charge" />
            </div>
          </label>
        )}

        {charges.map((c, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input
              placeholder="Concepto (ej. Limpieza extraordinaria)"
              value={c.concept}
              onChange={(e) => setCharge(i, "concept", e.target.value)}
              className="bg-[#F7F5F0] border-[#DCD8D0]"
              data-testid={`rs-charge-concept-${i}`}
            />
            <Input
              type="number" min="0" step="0.5" placeholder="€"
              value={c.amount}
              onChange={(e) => setCharge(i, "amount", e.target.value)}
              className="w-28 bg-[#F7F5F0] border-[#DCD8D0]"
              data-testid={`rs-charge-amount-${i}`}
            />
            <Button variant="ghost" size="sm" className="text-[#B54434]" onClick={() => setCharges((cs) => cs.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2 mt-1">
          <Button
            variant="outline" size="sm"
            onClick={() => setCharges((cs) => [...cs, { concept: "", amount: "" }])}
            className="border-[#DCD8D0]"
            data-testid="rs-add-charge"
          >
            <Plus className="h-4 w-4 mr-1" /> Añadir cargo
          </Button>
          {(r.expense_sheet?.items?.length || 0) > 0 && (
            <Button
              variant="outline" size="sm"
              className="border-[#DCD8D0] text-marca"
              data-testid="rs-import-expenses"
              onClick={() => {
                const sheetItems = r.expense_sheet.items.filter((i) => Number(i.amount) > 0);
                setCharges(sheetItems.map((i) => ({ concept: i.concept, amount: Number(i.amount) })));
                // La hoja ya trae su propia línea de km si procede: evitar duplicarla
                if (sheetItems.some((i) => i.kind === "km")) set("include_km_charge", false);
                toast.success(`${sheetItems.length} líneas importadas del control de gastos`);
              }}
            >
              Importar del control de gastos ({eur(r.expense_sheet.total)})
            </Button>
          )}
        </div>

        <div className="mt-5 bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-[#5C5C5C]">Fianza {isHold ? "preautorizada" : "cobrada"}</span><strong>{eur(deposit)}</strong></div>
          <div className="flex justify-between"><span className="text-[#5C5C5C]">Total cargos</span><strong className={total > 0 ? "text-[#B54434]" : ""}>{eur(total)}</strong></div>
          <div className="flex justify-between border-t border-[#DCD8D0] pt-1.5">
            <span className="text-[#5C5C5C]">{isHold ? "Se libera al cliente" : "Se devuelve al cliente"}</span>
            <strong className="text-[#4A7C59]">{eur(released)}</strong>
          </div>
          {over > 0 && (
            <div className="flex justify-between text-[#B54434]">
              <span>Exceso sobre fianza (cobro a tarjeta guardada)</span>
              <strong>{eur(over)}</strong>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <Label>Firma del propietario (conformidad de inspección)</Label>
        <div className="mt-2 max-w-md">
          <SignaturePad value={form.owner_signature} onChange={(v) => set("owner_signature", v)} testid="rs-signature" />
        </div>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button disabled={loading} className="w-full bg-marca hover:bg-marca-hover text-white h-12 text-base" data-testid="rs-settle-btn">
            <Wallet className="h-4 w-4 mr-2" /> {settleLabel}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar liquidación?</AlertDialogTitle>
            <AlertDialogDescription>
              {total <= 0
                ? `Se ${isHold ? "liberará la preautorización" : "devolverá la fianza"} de ${eur(deposit)} íntegramente y se notificará al cliente por email.`
                : `Se retendrán ${eur(kept)} (${charges.filter((c) => c.concept).length + (form.include_km_charge && kmCharge > 0 ? 1 : 0)} conceptos) y se ${isHold ? "liberarán" : "devolverán"} ${eur(released)} al cliente. ${over > 0 ? `Además se cobrarán ${eur(over)} a la tarjeta guardada. ` : ""}El cliente recibirá el desglose por email. Esta acción no se puede deshacer.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-marca hover:bg-marca-hover" data-testid="rs-settle-confirm">
              Liquidar fianza
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
